import { useAddressMap, zoomedIn } from 'components/common/AdressMap'
import { useGetPropertyDetail, useGetPropertyDetailWithObjectReferenceId, useGetSearchProperty, usePatchPropertyDetail } from 'dataQueries'
import { PropertyAddressDTO, PropertyType, RealEstatePropertyListItemDTO } from 'models/property'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useUserData } from 'components/contexts/UserDataContext'
import { Country } from 'constants/country'
import constate from 'constate'
import { Nullable } from 'models/helpers'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { lookupPlaceFromAddress } from 'utils/helpers/lookUpPlaceFromAddress'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { getFromGooglePlace, getPropertyAddressFromGooglePlace, GooglePlaceComponent } from 'utils/location/googlePlaceUtils'
import { logger } from 'utils/logging'

export const [PurchaseFlowRealEstatePropertyContextProvider, usePurchaseFlowRealEstateProperty] = constate(() => {
  const { userPropertyWorkspaceId } = useUserData()
  const { setZoom } = useAddressMap()

  const params = useBkbnSearchParams()

  const requestedPropertyId = useMemo(() => params.get('realtyId') ?? null, [params])
  const objectReferenceId = useMemo(() => params.get('objectReferenceId') ?? null, [params])
  const hasRequestedProperty = !!requestedPropertyId || !!objectReferenceId

  const getPropertyDetailQuery = useGetPropertyDetail(requestedPropertyId, userPropertyWorkspaceId, false)
  const getPropertyObjectReferenceQuery = useGetPropertyDetailWithObjectReferenceId(objectReferenceId, userPropertyWorkspaceId, false)

  // We want to use one query preferably with internal real estate property id
  const getPropertyQuery = useMemo(() => {
    if (requestedPropertyId) {
      return getPropertyDetailQuery
    } else {
      return getPropertyObjectReferenceQuery
    }
  }, [getPropertyDetailQuery, getPropertyObjectReferenceQuery, requestedPropertyId])

  // We want to use one property data preferably with internal real estate property id
  const requestedPropertyData = useMemo(() => {
    if (requestedPropertyId) {
      return getPropertyDetailQuery.data
    } else {
      return getPropertyObjectReferenceQuery.data
    }
  }, [getPropertyDetailQuery.data, getPropertyObjectReferenceQuery.data, requestedPropertyId])

  const [realEstatePropertyId, setRealEstatePropertyId] = useState<string | null>(null)
  const [isPropertyPrefilled, setIsPropertyPrefilled] = useState<boolean | null>(null)

  // Property address is prefilled if user comes from any property detail page with realtyId in patams
  const isPropertyPrefilledFlow = useMemo(() => {
    if (params.get('realtyId') && isPropertyPrefilled) return true
  }, [isPropertyPrefilled, params])

  // Check if user has to add property address to the order first (to have complete data about property)
  const updatePropertyAddressFlowDefault = useMemo(() => {
    if (!isPropertyPrefilledFlow) return false
    if (requestedPropertyData?.address) return false

    return true
  }, [isPropertyPrefilledFlow, requestedPropertyData?.address])

  const [isUpdatePropertyAddressFlow, setIsUpdatePropertyAddressFlow] = useState<boolean>(updatePropertyAddressFlowDefault)
  const [selectedPropertyType, setSelectedPropertyType] = useState<Nullable<PropertyType>>(null)
  const [selectedAssignmentPlace, setSelectedAssignmentPlace] = useState<GoogleAPIPlace | null>(null)
  const [searchProperty, setSearchProperty] = useState<string>('')
  const [selectedProperty, setSelectedProperty] = useState<Nullable<RealEstatePropertyListItemDTO>>(null)
  const [propertyName, setPropertyName] = useState<string>(requestedPropertyData?.title ?? '')
  const [isAddressManually, setIsAddressManually] = useState<boolean>(false)
  const [addressManual, setAddressManual] = useState<string>('')
  const [streetNumberManual, setStreetNumberManual] = useState<string>('')
  const [cityManual, setCityManual] = useState<string>('')
  const [zipCodeManual, setZipCodeManual] = useState<string>('')
  const [countryManual, setCountryManual] = useState<Country | string>('')

  const getSearchProperty = useGetSearchProperty(userPropertyWorkspaceId, { searchText: searchProperty })
  const searchPropertyData = useMemo(() => getSearchProperty.data?.data, [getSearchProperty.data])
  const searchPropertyContent = useMemo(() => searchPropertyData?.content?.filter((property) => property.address !== null), [searchPropertyData])

  const addressManualValue = useMemo(() => `${addressManual} ${streetNumberManual}, ${cityManual}, ${zipCodeManual}, ${countryManual}`, [addressManual, streetNumberManual, cityManual, zipCodeManual, countryManual])

  const realEstateProperty = useMemo(() => {
    let address: GoogleAPIPlace | null = null
    if (selectedAssignmentPlace) address = selectedAssignmentPlace

    return {
      title: selectedProperty ? selectedProperty?.title : propertyName,
      propertyType: selectedPropertyType,
      address: selectedProperty ? selectedProperty.address : getPropertyAddressFromGooglePlace(address ?? null),
    }
  }, [propertyName, selectedAssignmentPlace, selectedProperty, selectedPropertyType])

  const resetRealEstateProperty = useCallback(() => {
    setSearchProperty('')
    setSelectedProperty(null)
    setRealEstatePropertyId(null)
    setSelectedAssignmentPlace(null)
    params.delete('realtyId')
  }, [params])

  // prefill data on realtyId
  useEffect(() => {
    if (isPropertyPrefilled !== null) return

    if (!requestedPropertyId && !objectReferenceId) {
      setIsPropertyPrefilled(false)
      return
    }

    if (getPropertyQuery.isError) {
      setIsPropertyPrefilled(false)
      return
    }

    if (getPropertyQuery.isSuccess && requestedPropertyData) {
      setSelectedPropertyType(requestedPropertyData.propertyType)
      setRealEstatePropertyId(requestedPropertyData.id)

      setIsPropertyPrefilled(true)
      setSelectedProperty(requestedPropertyData)
    }

    // Omit "isPropertyPrefilled"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPropertyQuery.isError, getPropertyQuery.isSuccess, objectReferenceId, requestedPropertyData, requestedPropertyId])

  const { mutate: patchPropertyCountry } = usePatchPropertyDetail()
  const isResolvingCountryRef = useRef(false)

  const getSelectedPropertyPlace = useCallback(async () => {
    if (!selectedProperty) return
    if (!selectedProperty.address) return

    const { street, streetNumber, city, postalCode, country } = selectedProperty.address

    const place = await lookupPlaceFromAddress(`${street} ${streetNumber}, ${city}, ${postalCode}, ${country}`)
    setSelectedAssignmentPlace(place)
    setZoom(zoomedIn)
  }, [selectedProperty, setZoom])

  // Resolve country XX via geocoding when a property with unknown country is selected
  const resolvePropertyCountry = useCallback(async () => {
    if (!selectedProperty?.address) return
    if (selectedProperty.address.country !== 'XX') return

    const { street, streetNumber, postalCode, city } = selectedProperty.address

    // Skip if address fields are too sparse to geocode
    if (!street && !postalCode && !city) return

    isResolvingCountryRef.current = true

    try {
      const addressQuery = `${street} ${streetNumber}, ${postalCode} ${city}`
      const place = await lookupPlaceFromAddress(addressQuery)
      if (!place) return

      const countryCode = getFromGooglePlace(place, GooglePlaceComponent.country)
      if (!countryCode) return

      // Only accept supported countries
      if (!Object.values(Country).includes(countryCode as Country)) return

      // Build the patched address
      const patchedAddress: PropertyAddressDTO = {
        ...selectedProperty.address,
        country: countryCode,
      }

      // Also update coordinates if they were 0,0
      const coords = selectedProperty.address.coordinates
      if (!coords || (coords.lat === 0 && coords.lng === 0)) {
        patchedAddress.coordinates = {
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng,
        }
      }

      // Persist to backend (don't block)
      if (userPropertyWorkspaceId) {
        patchPropertyCountry(
          {
            workspaceId: userPropertyWorkspaceId,
            propertyId: selectedProperty.id,
            payload: { address: patchedAddress },
          },
          {
            onError: (error) => {
              logger.error('Failed to persist resolved country', error, {
                context: { component: 'PurchaseFlowRealEstateProperty', action: 'patchPropertyCountry' },
              })
            },
          }
        )
      }

      // Update local state — use functional updater to guard against stale closure
      const propertyId = selectedProperty.id
      setSelectedProperty((prev) => {
        if (prev?.id !== propertyId) return prev
        return { ...prev, address: patchedAddress }
      })
    } catch (error) {
      logger.error('Failed to resolve country for property', error, {
        context: { component: 'PurchaseFlowRealEstateProperty', action: 'resolvePropertyCountry' },
      })
    } finally {
      isResolvingCountryRef.current = false
    }
  }, [selectedProperty, userPropertyWorkspaceId, patchPropertyCountry])

  // Set real estate property id when selected property changes
  useEffect(() => {
    if (selectedProperty) {
      if (!isResolvingCountryRef.current) {
        getSelectedPropertyPlace()
      }
      setRealEstatePropertyId(selectedProperty.id)
      setPropertyName(requestedPropertyData?.title ?? selectedProperty.title ?? '')
      setSelectedAssignmentPlace(null)
    }
  }, [getSelectedPropertyPlace, requestedPropertyData?.title, selectedProperty])

  // Resolve country XX via geocoding when a property is selected
  useEffect(() => {
    if (!selectedProperty?.address) return
    if (selectedProperty.address.country !== 'XX') return

    resolvePropertyCountry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, selectedProperty?.address?.country])

  // Pre-fill manual address fields if isUpdatePropertyAddressFlow changes to true
  useEffect(() => {
    if (!isUpdatePropertyAddressFlow || !selectedAssignmentPlace) return
    const { street, streetNumber, city, postalCode, country } = getPropertyAddressFromGooglePlace(selectedAssignmentPlace) || {}
    setAddressManual([street, streetNumber].filter(Boolean).join(' ') || '')
    setCityManual(city || '')
    setZipCodeManual(postalCode || '')
    setCountryManual(country || '')

    // NOT use `selectedAssignmentPlace` as a dependency to ensure it doesnt run again when the place is set manually
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatePropertyAddressFlow])

  return {
    selectedPropertyType,
    setSelectedPropertyType,
    selectedAssignmentPlace,
    setSelectedAssignmentPlace,
    realEstateProperty,
    realEstatePropertyId,
    isPropertyPrefilled,
    requestedPropertyData,
    hasRequestedProperty,
    getPropertyQuery,
    searchProperty,
    setSearchProperty,
    getSearchProperty,
    selectedProperty,
    setSelectedProperty,
    searchPropertyData,
    searchPropertyContent,
    propertyName,
    setPropertyName,
    setRealEstatePropertyId,
    resetRealEstateProperty,
    isAddressManually,
    setIsAddressManually,
    addressManual,
    setAddressManual,
    streetNumberManual,
    setStreetNumberManual,
    cityManual,
    setCityManual,
    zipCodeManual,
    setZipCodeManual,
    countryManual,
    setCountryManual,
    addressManualValue,
    isUpdatePropertyAddressFlow,
    userPropertyWorkspaceId,
    isPropertyPrefilledFlow,
    setIsUpdatePropertyAddressFlow,
  }
})
